import React, { useEffect, useState } from "react"
import TextareaAutosize from "react-textarea-autosize"
import { formTranslation } from "../../../../locales/form"
import { useTranslation } from "react-i18next"
import styles from "./SimilarQuestionsContainer.module.scss"
import { FormCheck } from "react-bootstrap"
import LoadingButton from "../../../../components/LoadingButton/LoadingButton"
import debounce from "lodash/debounce"
import { useAppDispatch, useAppSelector } from "../../../../store/hooks"
import { getUnmarkedMessages } from "../../../../store/knowledgeBase/thunks"
import SimilarQuestionsList from "./SimilarQuestionsList/SimilarQuestionsList"
import {
    addAllUnmarkedMessageSelection,
    removeAllUnmarkedMessageSelection,
    resetUnmarkedMessageIfExist,
    resetUnmarkedMessagesSelection,
    updateArticleFormQuestionsFromSimilar
} from "../../../../store/knowledgeBase/slice"
import { testId } from "../../../../utility/tests/testId/knowledgeBase"
import { selectSimilarQuestionList, similarQuestionsSelection } from "../../../../store/knowledgeBase/selectors"
import Async from "../../../../components/Async/Async"
import AdvancedSkeleton from "../../../../components/Skeleton/AdvancedSkeleton"
import ErrorMessage from "../../../../components/ErrorMessage/ErrorMessage"

const tKnowledgeBaseNamespace = "knowledgeBase"

export interface SimilarQuestionsContainerProps {
    closeSidebar: () => void
}

export const SIMILAR_QUESTIONS_PAGE_SIZE = 50

const SimilarQuestionsContainer = (props: SimilarQuestionsContainerProps) => {
    const { closeSidebar } = props
    const { t } = useTranslation(tKnowledgeBaseNamespace)
    const dispatch = useAppDispatch()
    const messages = useAppSelector(selectSimilarQuestionList)
    const isLoaded = messages.data && !messages.inProcess
    const [text, setText] = useState("")
    const selectedQuestions = useAppSelector(similarQuestionsSelection)
    const messagesLength = messages.data ? Object.keys(messages.data).length : 0
    const selectedLength = Object.keys(selectedQuestions).length
    const checked = selectedLength !== 0 && messagesLength === selectedLength
    const displayCondition = Boolean(isLoaded && messagesLength !== 0)

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            dispatch(addAllUnmarkedMessageSelection())
        } else {
            dispatch(removeAllUnmarkedMessageSelection())
        }
    }

    const handleAddNewQuestions = () => {
        dispatch(updateArticleFormQuestionsFromSimilar())
        closeSidebar()
    }

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value.trim()
        dispatch(getUnmarkedMessages(text, SIMILAR_QUESTIONS_PAGE_SIZE))
        setText(text)
    }

    useEffect(() => {
        dispatch(getUnmarkedMessages("", SIMILAR_QUESTIONS_PAGE_SIZE))
    }, [])

    return (
        <div className={styles.questionsContainer} data-testid={testId.similarQuestionsContainer}>
            <TextareaAutosize
                placeholder={t(formTranslation.search)}
                onChange={debounce(handleTextChange, 1000)}
                maxRows={5}
                className={styles.questionsContainer_input}
                data-testid={testId.similarQuestionsInput}
            />
            <div className={styles.questionsContainer_tools}>
                {displayCondition && (
                    <FormCheck
                        custom
                        id="select-similar-all"
                        type="checkbox"
                        onChange={handleCheckboxChange}
                        checked={checked}
                        disabled={false}
                        label={t(formTranslation.selectAll)}
                        className={styles.questionsContainer_tools_checkbox}
                        data-testid={testId.similarQuestionsCheckbox}
                    />
                )}
            </div>
            <Async
                dataState={messages}
                processView={<AdvancedSkeleton count={6} />}
                errorView={({ message }) => <ErrorMessage text={message} />}
                nonDataView={<div className={styles.questionsContainer_empty}>{t(`similar-questions.no-data`)}</div>}
                validator={data => {
                    return displayCondition
                }}
            >
                {({ data }) => <SimilarQuestionsList text={text} messages={data} />}
            </Async>
            {displayCondition && (
                <LoadingButton className={styles.questionsContainer_button} onClick={handleAddNewQuestions}>
                    {t(`similar-questions.add`)}
                </LoadingButton>
            )}
        </div>
    )
}

export default SimilarQuestionsContainer
